"use client";
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { supabase } from '@/utils/supabase/client';
import { AuthActivityService } from '@/utils/auth/authActivityService';
import { cleanupForeignSupabaseAuthStorage, cleanupUserLocalStorage } from '@/utils/auth/localStorageCleanup';
import { clearDoorStorage } from '@/components/events/checkin/doorStore';
import { validateInternalPath } from '@/utils/url/validateInternalPath';
import { revokeDemoAccessOnSignOut } from '@/utils/demo/revokeDemoAccessOnSignOut';
import { disarmPreviewFetchGuard } from '@/utils/enrollmentPreview/previewFetchGuard';
import { clearSessionLost, currentSessionGeneration, isIntentionalSignOut, markIntentionalSignOut, markSessionLost } from '@/utils/auth/sessionLost';
import { confirmSessionGone } from '@/utils/auth/authedFetch';
import { ROLE_SELECTION_PENDING_VALUE } from '@/utils/onboarding/roleSelectionGate';

import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('AuthContext');

// REQUIREMENTS SUMMARY:
// - Auth transitions must clear stale local Supabase project sessions before
//   sign-in/sign-out so local Docker and hosted-project cookies never mix.

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** Alias for isLoading - used by travel/expenses and other pages */
  loading?: boolean;
  // requiresOnboarding IS returned and IS read by SignInForm. It was missing from this
  // type, so the compiler never checked that branch — a rename or typo would silently
  // yield undefined and drop a new staff member into the app with no role selected.
  // (This PR removed a sibling field from the same shape, which is exactly the edit that
  // would have caused it.)
  signIn: (email: string, password: string) => Promise<{
    success: boolean;
    error: Error | null;
    requiresPasswordChange?: boolean;
    requiresOnboarding?: boolean;
  }>;
  /**
   * Signs the user out and sends them to `redirectTo` (default /signin).
   * The parent-view staff notice passes the gym's public program page so a
   * staff member creating a separate personal account lands where they can
   * sign up, instead of on the login screen they just left.
   */
  signOut: (redirectTo?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Initialize auth state from Supabase
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        cleanupForeignSupabaseAuthStorage(process.env.NEXT_PUBLIC_SUPABASE_URL);
        
        logger.info("🔐 [AuthContext] Initializing auth state...");
        
        // Get current session - handle errors gracefully
        try {
          const { data: sessionData, error } = await supabase.auth.getSession();
          
          if (error) {
            logger.warn("🔐 [AuthContext] Session check error:", error.message);
            // Don't clear session on generic errors, only if explicitly unauthenticated
            if (error.message.includes('expired') || error.message.includes('not found')) {
              setSession(null);
              setUser(null);
            }
          } else {
            const session = sessionData.session;
            
            // 🔥 SAFETY CHECK: Ensure session is actually an object
            if (session && typeof session === 'string') {
              logger.error('🔐 [AuthContext] CRITICAL: Initial session received as string!', session);
              setSession(null);
              setUser(null);
            } else {
              logger.info("🔐 [AuthContext] Session data retrieved", session ? `Session exists for ${session?.user?.email}` : "No session");
              setSession(session);
              setUser(session?.user || null);
            }
          }
        } catch (sessionError) {
          logger.warn("🔐 [AuthContext] Session check failed:", sessionError);
        }
        
        // Listen for auth changes - handle auth errors gracefully
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            logger.info(`🔐 [AuthContext] Auth state changed: ${event}, user: ${session?.user?.email || 'none'}`);
            
            // 🔥 SAFETY CHECK: Ensure session is actually an object before using it.
            // Some edge cases in Supabase storage can cause session to be a string.
            if (session && typeof session === 'string') {
              logger.error('🔐 [AuthContext] CRITICAL: Session received as string instead of object!', session);
              return;
            }

            // 🔥 FIX: Only update state if session actually changed or is valid
            // This prevents "flickering" to null during transient refresh states
            if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
              // A deliberate sign-out clears state immediately so the
              // redirect to /signin is instant. (No USER_DELETED case: the
              // installed @supabase/auth-js never emits it — the outer
              // comparison above is pre-existing dead code.)
              if (isIntentionalSignOut()) {
                setSession(null);
                setUser(null);
                return;
              }

              // #2700: tell the user they were signed out instead of leaving
              // them on a page that has quietly stopped loading.
              //
              // RE-CHECK BEFORE BELIEVING IT. `SIGNED_OUT` fires transiently
              // during ordinary session sync — utils/webview/sessionMonitor.ts
              // has guarded against exactly this for the mobile webview since
              // before this code existed, and the same race reaches the web.
              // `confirmSessionGone()` treats an unreachable auth service as
              // "still signed in", so a provider slowdown cannot raise a false
              // banner. Generation captured before the await — a slow "yes,
              // gone" resolving after the session returned must not re-raise.
              //
              // The React state clear waits for the SAME confirmation. Clearing
              // it synchronously here made AdminClientLayout swap the whole
              // admin shell to its "Redirecting to login..." screen for the
              // instant before the session event restored it — the random
              // full-page flash reported 2026-08-19. The guard on the banner
              // and the guard on the screen must move together.
              const generation = currentSessionGeneration();
              void confirmSessionGone().then((gone) => {
                if (!gone) return;
                // Session came back while we were checking — a newer event
                // already restored state; a stale "gone" must not clear it.
                if (generation !== currentSessionGeneration()) return;
                setSession(null);
                setUser(null);
                markSessionLost(generation);
              });
            } else if (session) {
              // #2700 FIX (review finding): clearing only inside signIn() was
              // not enough and made the signal LATCH. Any event that arrives
              // WITH a session means we are signed in again — SIGNED_IN,
              // TOKEN_REFRESHED, INITIAL_SESSION, or another tab restoring the
              // shared cookie (exactly the 23:13 flap seen in production).
              //
              // Two real bugs this closes:
              //  - the banner kept telling a demonstrably signed-in user they
              //    were signed out, and `authedFetch` kept refusing to send, so
              //    the converted polling hooks stayed dead after recovery — a
              //    REGRESSION versus main.
              //  - signing back in via the emailed code path goes through
              //    verifyOtp, NOT signIn(), so the intentional-sign-out marker
              //    never cleared and the banner was suppressed for that tab's
              //    whole life — silently reinstating #2700.
              clearSessionLost();
              setSession(session);
              // Only update user if the user ID changed to prevent unnecessary re-renders
              // on TOKEN_REFRESHED events (e.g. when browser tab regains focus)
              setUser(prev => prev?.id === session.user.id ? prev : session.user);
            }
          }
        );
        
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        logger.error('AuthContext: Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // 🔥 REDUNDANT REFRESH REMOVED: 
  // Supabase's autoRefreshToken handled by the client is sufficient and more reliable.
  // The manual interval was causing race conditions (refresh_token_already_used) 
  // between multiple tabs and API middleware.

  // Sign in function
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      logger.info(`AuthContext: Attempting to sign in with email: ${email}`);
      cleanupUserLocalStorage();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        logger.error('AuthContext: Sign in error:', error);
        throw error;
      }
      
      logger.info('AuthContext: Sign in successful, user:', data.user?.email);
      
      // Safety check: Verify email is confirmed (Supabase should block this, but double-check)
      if (data.user && !data.user.email_confirmed_at) {
        logger.warn('AuthContext: User signed in but email not verified - signing out immediately');
        await supabase.auth.signOut({ scope: 'local' });
        setSession(null);
        setUser(null);
        throw new Error('Email not confirmed. Please verify your email before signing in.');
      }
      
      setSession(data.session);
      setUser(data.session?.user || null);

      // #2700: a successful sign-in is the real "we are signed in again"
      // moment — clear both the lost-session flag and the intentional
      // sign-out marker so neither can leak into the new session.
      clearSessionLost();

      // Check if user needs to change password
      const passwordResetRequired = data.user?.user_metadata?.password_reset_required;
      const resetByAdmin = data.user?.user_metadata?.password_reset_by_admin;

      if (passwordResetRequired && resetByAdmin) {
        logger.info('AuthContext: User requires mandatory password change');
        return { 
          success: true, 
          error: null, 
          requiresPasswordChange: true 
        };
      }

      // Check if user needs to complete onboarding (role selection)
      if (data.user) {
        // Staff can work at more than one gym, and onboarding_completed is per-gym.
        // A bare maybeSingle() here 406s (PGRST116) for anyone with 2+ rows, which
        // silently skipped role selection for every multi-gym staffer (#2340). Ask the
        // narrower question instead: is there a gym where they still owe a role choice?
        //
        // The status = 'active' filter is DELIBERATE and must stay identical to the
        // matching filter in src/app/api/onboarding/complete-role-selection/route.ts —
        // this gate and that write have to resolve the same row (#2340).
        // organization_staff.status also allows 'pending', which both sides
        // intentionally exclude: we have not yet decided whether 'pending' means
        // "invited but not accepted" (correctly skipped) or "on staff but awaiting
        // something else" (should be gated). See #2522. If that decision widens the
        // filter, widen it at BOTH sites in the same change.
        //
        // Likewise, "still owes a role choice" is ROLE_SELECTION_PENDING_VALUE from
        // @/utils/onboarding/roleSelectionGate — the same constant the route's
        // firstPending() compares against. That shared constant is what keeps the two
        // halves agreeing by construction rather than by data (#2525 §4); the route
        // used to accept NULL here while this .eq() never did.
        const { data: staffRecord, error: staffError } = await supabase
          .from('organization_staff')
          .select('onboarding_completed')
          .eq('auth_user_id', data.user.id)
          .eq('status', 'active')
          .eq('onboarding_completed', ROLE_SELECTION_PENDING_VALUE)
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (staffError) {
          // Fail open, as this has always done — a lookup problem must not block sign-in.
          logger.error('AuthContext: Could not check onboarding status', staffError);
        }

        // The query already filters to pending rows, so a row here IS the gate.
        if (staffRecord) {
          logger.info('AuthContext: User requires onboarding (role selection)');
          return {
            success: true,
            error: null,
            requiresPasswordChange: false,
            requiresOnboarding: true,
          };
        }

        // NOTE: sign-in used to fetch organizations.onboarding_completed_at here and
        // return requiresGymSetup, purely so SignInForm could redirect owners with
        // unfinished settings to the settings checklist. That redirect is gone — a new
        // owner lands on the dashboard with the roster guide — so the flag had no
        // consumer left, and this query ran on every single sign-in to feed it.
      }

      // NOTE: Login activity tracking moved to Phase 2 multi-org login flow
      // Activity will be tracked when user selects organization after login
      
      return { success: true, error: null, requiresPasswordChange: false, requiresOnboarding: false };
    } catch (error) {
      logger.error('AuthContext: Error signing in:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown error occurred') 
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sign out function
  const signOut = useCallback(async (redirectTo: string = '/signin') => {
    // Same shared validator the signin/signup ?redirectTo= flows use — it was
    // hardened for the browser-strips-control-characters case (#1785), so a
    // second copy here would miss the next hardening.
    const destination = validateInternalPath(redirectTo, '/signin');

    try {
      setIsLoading(true);
      logger.info('AuthContext: Signing out...');

      // #2700: Supabase fires the same SIGNED_OUT event whether the user chose
      // to sign out or their session evaporated. Mark this one as deliberate
      // BEFORE calling signOut() so the "You've been signed out" banner does
      // not flash on the way to /signin.
      markIntentionalSignOut();

      // A staff "Preview as a parent" refuses every write; leaving the account ends the preview.
      disarmPreviewFetchGuard();

      // Drop the demo-viewer pass while the session is still valid. Otherwise
      // CheerVue Demo Gym stays on the gym picker after the next login.
      try {
        const { data } = await supabase.auth.getSession();
        await revokeDemoAccessOnSignOut(data.session?.access_token ?? null);
      } catch (demoErr) {
        logger.warn('Demo access revoke during sign-out failed; continuing with sign-out', demoErr);
      }

      // Clean up localStorage entries for security. This clears the auth
      // cookies too, and those ARE the session store (createBrowserClient), so
      // from here on sign-out is one-way: the call below has nothing left to
      // read, cannot reach the server, and cannot meaningfully fail. Do not add
      // a failure path for it — there is no state to report back from.
      cleanupUserLocalStorage();
      // Door check-in guest lists (event tickets) never outlive the sign-in.
      // Unsent check-ins stay for this user's next sign-in (doorStore.ts).
      void clearDoorStorage().catch(() => undefined);

      await supabase.auth.signOut({ scope: 'local' });
      setSession(null);
      setUser(null);
      
      // Use replace instead of push to prevent back navigation to authenticated pages
      logger.info(`AuthContext: Sign out successful, redirecting to ${destination}`);
      if (destination === '/signin') {
        router.replace(destination);
      } else {
        // A soft navigation loses this race: the (admin) layout stays mounted
        // while the new route loads, sees user===null with authIsLoading===false,
        // and fires its own router.replace to /signin (AdminClientLayout's
        // ProtectedRouteWrapper). A full page load leaves nothing behind to do
        // that. Only /signin can be reached softly, because landing there is
        // what the layout wanted anyway.
        window.location.assign(destination);
      }
    } catch (error) {
      logger.error('AuthContext: Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      setIsLoading(true);
      logger.info('AuthContext: Refreshing session...');
      const { data: { session } } = await supabase.auth.refreshSession();
      setSession(session);
      // Use same ID-check as onAuthStateChange to avoid unnecessary re-renders
      setUser(prev => {
        const newUser = session?.user || null;
        return prev?.id === newUser?.id ? prev : newUser;
      });
      logger.info('AuthContext: Session refresh complete', session ? "Session valid" : "No valid session");
    } catch (error) {
      logger.error('AuthContext: Error refreshing session:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders on token refresh
  const value = useMemo(() => ({
    user,
    session,
    isLoading,
    loading: isLoading,
    signIn,
    signOut,
    refreshSession,
  }), [user, session, isLoading, signIn, signOut, refreshSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
